<template>
    <v-form @submit.prevent="submitForm" ref="form">
        <v-container>
            <v-row>
                <v-col cols="12" md="12">
                    <v-file-input label="Choose CSV file to batch process" variant="outlined" @change="fileChanged"
                        :disabled="submitDisabled" />
                    <v-btn type="submit" block append-icon="mdi-upload" :disabled="submitDisabled"
                        :loading="uploadInProgress">Submit</v-btn>
                </v-col>
            </v-row>
            <v-row v-if="message">
                <v-col cols="12" md="12">
                    <h4 style="padding-top: 30px; padding-bottom: 15px;"
                        :class="uploadSuccessful ? 'text-green' : 'text-red'">
                        {{ uploadSuccessful ? "Success!" : "Upload failed" }}
                    </h4>
                    <v-code>{{ message }}</v-code>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="12">
                    <v-btn v-if="doneVisible" prepend-icon="mdi-check-circle" @click="doneClicked">Done</v-btn>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useAuth0 } from "@auth0/auth0-vue";
import axios from "axios";

const form = ref<HTMLFormElement | null>(null);
const file = ref<File | null>(null);
const message = ref<string>("");
const submitDisabled = ref<boolean>(false);
const uploadInProgress = ref<boolean>(false);
const uploadSuccessful = ref<boolean>(false);
const doneVisible = ref<boolean>(false);

const { getAccessTokenSilently } = useAuth0();
const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;


const fileChanged = (event: Event) => {
    let target = event.target as HTMLInputElement;
    let f = target.files?.item(0);
    if (f) {
        file.value = f as File;
    }
};

const doneClicked = async () => {
    file.value = null;
    message.value = "";
    submitDisabled.value = false;
    doneVisible.value = false;
    form.value?.reset();
}

const submitForm = async () => {
    const accessToken = await getAccessTokenSilently();
    const formData = new FormData();
    formData.append("file", file.value as File);

    axios({
        url: `${apiServerUrl}/api/batch_upload`,
        method: "POST",
        headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${accessToken}`,
        },
        data: formData,
        responseType: "blob",
        timeout: 300000, // 5 minutes
    })
        .then((response) => {
            const blob = new Blob([response.data], { type: response.data.type });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'unknown';
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                if (fileNameMatch && fileNameMatch.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.value = "File downloaded successfully";
            uploadSuccessful.value = true;
            doneVisible.value = true;
            uploadInProgress.value = false;
        })
        .catch((error) => {
            message.value = error.message;
            if (error.detail) {
                message.value += ": " + error.detail;
            }
            uploadSuccessful.value = false;
            doneVisible.value = true;
            uploadInProgress.value = false;

        });

    submitDisabled.value = true;
    uploadInProgress.value = true;

};

</script>
